import { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import './App.css';
import ravenVideo from './assets/background.mp4';
import ravenLogo from './assets/raven-logo.png';
import Privacy from './Privacy';
import Terms from './Terms';
import Announcement from './Announcement';
import FeaturedApp from './FeaturedApp';

const DOCS_URL = 'https://docs.raven.market/';
const TWITTER_URL = 'https://x.com/RavenMarkets';
const TELEGRAM_URL = 'https://t.me/RavenMarkets';
const GITHUB_URL = 'https://github.com/RiveraMoney';

const ROUTE_META = {
  '/': {
    title: 'Raven Market — Digital Options on Canton · Featured App',
    description: 'Raven, the first digital options protocol on the Canton Network and a Canton Featured App, has been acquired by Premia. Read the announcement.',
  },
  '/featured-app': {
    title: 'Raven Becomes a Featured App on Canton Network',
    description: 'Raven Market is a Featured App on the Canton Network — a designation tied to real, fee-generating activity on the network, not marketing.',
  },
  '/announcement': {
    title: 'Raven Market is Acquired by Premia',
    description: 'Raven Market has been acquired by Premia in an all-cash transaction. Its technology will be integrated into Premia’s institutional platform on the Canton Network.',
  },
  '/privacy': {
    title: 'Privacy Policy — Raven Market',
    description: 'Privacy policy for Raven Market, the first digital options protocol on the Canton Network.',
  },
  '/terms': {
    title: 'Terms of Service — Raven Market',
    description: 'Terms of service for Raven Market, the first digital options protocol on the Canton Network.',
  },
};

function RouteMeta() {
  const { pathname } = useLocation();
  useEffect(() => {
    const meta = ROUTE_META[pathname] || ROUTE_META['/'];
    document.title = meta.title;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', meta.description);
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', 'https://raven.market' + (pathname === '/' ? '/' : pathname));
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  const [ccPrice, setCcPrice] = useState('');
  const [pricesVisible, setPricesVisible] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const errorCount = useRef(0);

  useEffect(() => {
    const fetchPrice = async () => {
      try {
        const response = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=canton-network&vs_currencies=usd');
        const data = await response.json();
        const cc = data['canton-network']?.usd;
        if (cc != null) {
          setCcPrice(cc.toFixed(4));
          errorCount.current = 0;
          setPricesVisible(true);
        } else {
          throw new Error('incomplete data');
        }
      } catch (error) {
        console.error('Error fetching price:', error);
        errorCount.current += 1;
        if (errorCount.current >= 2) setPricesVisible(false);
      }
    };
    fetchPrice();
    const interval = setInterval(fetchPrice, 90000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const favicon = document.getElementById('favicon');
    const appleIcon = document.getElementById('apple-icon');
    const matcher = window.matchMedia('(prefers-color-scheme: dark)');

    const update = (isDark) => {
      const href = isDark ? '/dark-icon.png' : '/icon.png';
      if (favicon) favicon.setAttribute('href', href);
      if (appleIcon) appleIcon.setAttribute('href', href);
    };

    update(matcher.matches);
    matcher.addEventListener('change', (e) => update(e.matches));
    return () => matcher.removeEventListener('change', (e) => update(e.matches));
  }, []);

  const layout = (children, showClose = false) => (
    <div className={`landing${menuOpen ? ' menu-open' : ''}`}>
      <div className="bg">
        <video className="bg-video" src={ravenVideo} autoPlay muted playsInline />
        <div className="overlay" />
      </div>

      <div className="ui-layer">
        {!showClose && (
          <Link to="/announcement" className="announcement-banner">
            <span className="banner-tag">NEWS</span>
            RAVEN HAS BEEN ACQUIRED BY PREMIA
            <span className="banner-cta">READ THE ANNOUNCEMENT →</span>
          </Link>
        )}
        <nav className="navbar">
          <div className="nav-mobile-left">
            {!showClose && (
              <button className="hamburger" onClick={() => setMenuOpen(true)} aria-label="Open menu">
                <div className="bar" />
                <div className="bar" />
                <div className="bar" />
              </button>
            )}
          </div>
          <Link to="/"><img src={ravenLogo} alt="Raven Market" className="nav-logo" /></Link>
          <div className="nav-right">
            {!showClose && (
              <Link className="nav-featured" to="/featured-app">
                <span className="nav-featured-star">★</span>
                <span className="nav-featured-text">FEATURED APP · CANTON</span>
              </Link>
            )}
            {showClose && <Link to="/" className="nav-close">✕</Link>}
          </div>
        </nav>

        {children}

        <footer className="footer">
          <div className="footer-left">
            <Link to="/privacy">PRIVACY POLICY</Link>
            <Link to="/terms">TERMS OF USE</Link>
          </div>
          <div className="social-links">
            <a href={TWITTER_URL} target="_blank" rel="noreferrer">
              <img src="/x.png" alt="X" className="social-icon-img" /><span className="social-label">TWITTER</span>
            </a>
            <a href={TELEGRAM_URL} target="_blank" rel="noreferrer">
              <img src="/tg.png" alt="Telegram" className="social-icon-img" /><span className="social-label">TELEGRAM</span>
            </a>
            <a href={GITHUB_URL} target="_blank" rel="noreferrer">
              <img src="/gthb.png" alt="GitHub" className="social-icon-img" /><span className="social-label">GITHUB</span>
            </a>
            <a href={DOCS_URL} target="_blank" rel="noreferrer">
              <img src="/docs.png" alt="Docs" className="social-icon-img" /><span className="social-label">DOCS</span>
            </a>
          </div>
          {pricesVisible && (
            <div className="footer-right">
              <div className="price-row live">CC/USD <strong>${ccPrice}</strong></div>
            </div>
          )}
        </footer>

        {menuOpen && (
          <div className="mobile-menu-overlay">
            <div className="mobile-menu-header">
              <button className="close-menu" onClick={() => setMenuOpen(false)}>×</button>
              <img src={ravenLogo} alt="Raven Market" className="mobile-menu-logo" />
            </div>
            <div className="mobile-menu-content">
              <Link to="/announcement" onClick={() => setMenuOpen(false)}>ANNOUNCEMENT</Link>
              <Link to="/featured-app" onClick={() => setMenuOpen(false)}>FEATURED APP</Link>
              <a href={DOCS_URL} target="_blank" rel="noreferrer">DOCS</a>
              <a href={TWITTER_URL} target="_blank" rel="noreferrer">TWITTER</a>
              <a href={TELEGRAM_URL} target="_blank" rel="noreferrer">TELEGRAM</a>
              <a href={GITHUB_URL} target="_blank" rel="noreferrer">GITHUB</a>
              <hr />
              <Link to="/privacy" onClick={() => setMenuOpen(false)}>PRIVACY POLICY</Link>
              <Link to="/terms" onClick={() => setMenuOpen(false)}>TERMS OF USE</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const hero = (
    <main className="hero">
      <Link className="nav-featured hero-featured" to="/featured-app">
        <span className="nav-featured-star">★</span>
        FEATURED APP · CANTON
      </Link>
      <h1 className="headline">
        <span className="headline-white">Market Argues,</span>
        <br />
        <span className="headline-yellow">Raven Settles.</span>
      </h1>
      <p className="subtitle">
        <span className="subtitle-dim">THE FIRST </span>
        <span className="subtitle-bright">DIGITAL OPTIONS PROTOCOL</span>
        <span className="subtitle-dim"> ON CANTON</span>
      </p>
      <Link to="/announcement" className="cta-button cta-link">READ THE ANNOUNCEMENT</Link>
      <p className="network-label">CANTON NETWORK · NOW PART OF PREMIA</p>
    </main>
  );

  return (
    <Router>
      <RouteMeta />
      <Routes>
        <Route path="/" element={layout(hero)} />
        <Route path="/announcement" element={layout(<Announcement />, true)} />
        <Route path="/featured-app" element={layout(<FeaturedApp />, true)} />
        <Route path="/privacy" element={layout(<Privacy />, true)} />
        <Route path="/terms" element={layout(<Terms />, true)} />
      </Routes>
    </Router>
  );
}

export default App;
